import React, { useMemo, useState } from 'react';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import {
  Alert,
  type AlertDetails,
  Loading,
  SearchField,
} from '../../../../components';
import {
  useUpdateApplicantInformation,
  type ApplicantInformation,
} from '../../../UserInfo/graphql';
import { UserInformationForm } from '../../../UserInfo/components';
import { useGetApplicantByFilter } from '../graphql/useGetApplicantByFilter';
import { IS_LOCAL, NEED_STUDENT_VISA } from '../../../Signup/components';
import { VIEW_MODE } from '../../../../utils';

export const ApplicantInformationUpdateHomePage: React.FC<any> = () => {
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const [applicant, setApplicant] = useState<ApplicantInformation | null>(null);
  const [shallowLoading, setShallowLoading] = useState(false);
  const [searchContent, setSearchContent] = useState('');

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const updateApplicantInformation = useUpdateApplicantInformation();

  const handleContentChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchContent(event.target.value);
  };

  const handleSearch = async (): Promise<void> => {
    setShallowLoading(true);

    await useGetApplicantByFilter(client, {
      email: { eq: searchContent },
    })
      .then(applicant => {
        if (applicant.id as boolean) {
          setApplicant({
            ...applicant,
            isLocal: (applicant.isLocal as string)
              ? IS_LOCAL.LOCAL
              : IS_LOCAL.NON_LOCAL,
            needStudentVisa: (applicant.needStudentVisa as string)
              ? NEED_STUDENT_VISA.YES
              : NEED_STUDENT_VISA.NO,
          });
        } else {
          setAlertDetails({
            alertContent: `No Record.`,
            alertType: 'warning',
          });
          setOpenAlert(true);
          setApplicant(null);
        }
      })
      .catch(error => {
        setAlertDetails({
          alertContent: `Error Occurs. ${(error as Error).message}`,
          alertType: 'error',
        });
        setOpenAlert(true);
        setApplicant(null);
      })
      .finally(() => {
        setShallowLoading(false);
      });
  };

  const handlePreprocessData = (data: any): { id: string; input: any } => {
    const {
      id,
      surname,
      givenName,
      chineseName,
      identityDocumentNumber,
      identityDocumentType,
      identityType,
      isLocal,
      needStudentVisa,
      telephoneNumber,
      disabilitySpecialType,
      disabilitySpecialDetail,
      ...others
    } = data;

    const input: ApplicantInformation = {
      name: { surname, givenName, chineseName },
      identity: {
        identityType,
        identityDocumentNumber,
        identityDocumentType,
        isLocal: isLocal === IS_LOCAL.LOCAL,
        needStudentVisa: needStudentVisa === NEED_STUDENT_VISA.YES,
      },
      ...(disabilitySpecialType !== 'NA' && disabilitySpecialType !== undefined
        ? {
            disabilitySpecial: {
              disabilitySpecialType,
              disabilitySpecialDetail,
            },
          }
        : { disabilitySpecial: null }),
      ...(telephoneNumber !== '' && telephoneNumber !== null
        ? { telephoneNumber }
        : null),
      ...others,
    };
    return { id, input };
  };

  const handleUpdateApplicant = async (
    applicant: ApplicantInformation,
  ): Promise<void> => {
    setShallowLoading(true);

    const { id, input } = handlePreprocessData(applicant);

    await updateApplicantInformation({
      variables: {
        id,
        input,
      },
    })
      .then(res => {
        return res?.data?.updateApplicant;
      })
      .then(applicant => {
        if (applicant._id === id) {
          setAlertDetails({
            alertContent: 'User Information Updated Successfully.',
            alertType: 'success',
          });
        } else {
          setAlertDetails({
            alertContent: 'Cannot Update User Information.',
            alertType: 'error',
          });
        }
      })
      .catch(error => {
        setAlertDetails({
          alertContent: `Error Updating User Information. ${
            (error as Error).message
          }`,
          alertType: 'error',
        });
      })
      .finally(() => {
        setOpenAlert(true);
        setShallowLoading(false);
      });
  };

  const applicantInfo = useMemo(() => {
    return (
      applicant && (
        <UserInformationForm
          key={searchContent}
          initialValues={applicant}
          handleUpdateApplicant={handleUpdateApplicant}
          viewMode={VIEW_MODE.SUPER_ADMIN}
        />
      )
    );
  }, [applicant]);

  return (
    <>
      {shallowLoading && <Loading />}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <div className="flex flex-row justify-center">
        <div className={`${applicant ? 'pt-8' : 'py-8'} px-[7rem] w-full`}>
          <SearchField
            disabled={searchContent.length < 1}
            content={searchContent}
            placeholder={"Applicant's Email"}
            handleContentChange={handleContentChange}
            handleSearch={handleSearch}
          />
        </div>
      </div>
      {applicantInfo}
    </>
  );
};
